/**
 * Local persistence, namespaced per signed-in user.
 *
 * The legacy app wrote to bare keys like `mbmstudio_trips`. On a shared
 * computer — or simply after switching accounts — one advisor could load
 * another's cached data. Every key here is prefixed with the uid.
 *
 * Two rules for anything stored here:
 *   1. It is a CACHE, never the source of truth. If losing it would lose
 *      customer data, it belongs in Firestore.
 *   2. Never store anything large. Base64 images in localStorage are what
 *      forced the legacy logo to be excluded from sync entirely.
 */

const PREFIX = 'mbm'

let namespace = 'anon'

/**
 * Point the store at a user. Call on sign-in and sign-out.
 * @param {string} uid
 */
export function setNamespace(uid) {
  namespace = uid || 'anon'
}

/** @param {string} key */
function fullKey(key) {
  return `${PREFIX}:${namespace}:${key}`
}

/**
 * @param {string} key
 * @param {any} [fallback]
 * @returns {any}
 */
export function get(key, fallback = null) {
  try {
    const raw = localStorage.getItem(fullKey(key))
    return raw == null ? fallback : JSON.parse(raw)
  } catch {
    return fallback
  }
}

/**
 * @param {string} key
 * @param {any} value
 * @returns {boolean} false when the write failed (quota, private browsing)
 */
export function set(key, value) {
  try {
    localStorage.setItem(fullKey(key), JSON.stringify(value))
    return true
  } catch (err) {
    console.warn('local store write failed', key, err)
    return false
  }
}

/** @param {string} key */
export function remove(key) {
  try {
    localStorage.removeItem(fullKey(key))
  } catch {
    /* nothing useful to do */
  }
}

/** Clear every key belonging to the current user. */
export function clearNamespace() {
  try {
    const prefix = `${PREFIX}:${namespace}:`
    const doomed = Object.keys(localStorage).filter((k) => k.startsWith(prefix))
    for (const key of doomed) localStorage.removeItem(key)
  } catch (err) {
    console.warn('local store clear failed', err)
  }
}
